import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Link, useHistory } from 'react-router-dom'
import Component from '../../components/common/Component'
import { sha256 } from '../../utils/functions'
import Snackbar from '@mui/material/Snackbar'
import MuiAlert from '@mui/material/Alert'
import { createAccount } from '../../api/account'
import { useContext } from 'react'
import AppContext, { JWT } from '../../app/AppContext'
import Cookies from 'js-cookie'
import jwt from 'jsonwebtoken'
import { Checkbox } from '@mui/material'

type SnackbarSeverity = 'success' | 'warning' | 'error' | 'info'
type SnackbarInput = {
  open: boolean
  severity?: SnackbarSeverity
  message?: string | undefined
}

export default function CreateAccount(): JSX.Element {
  const history = useHistory()
  const { appReducer } = useContext(AppContext)

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [checkPassword, setCheckPassword] = useState('')
  const [checked, setChecked] = useState<boolean>(false)
  const [snackbar, setSnackbar] = useState<SnackbarInput>({ open: false })
  const handleSnackBarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }
  const handleReturn = () => {
    history.push('/login')
  }

  const isValidEmail = (email: string): boolean => {
    const validEmail = email
      ? email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) !== null
      : true
    if (validEmail) {
      setEmailError('')
    } else {
      setEmailError('Please insert a valid email')
    }
    return validEmail
  }

  const isValidPassword = (pw: string): boolean => {
    const MIN_PASSWORD_LENGTH = 8
    const validPassword = pw
      ? /[A-Z]/.test(pw) &&
        /[a-z]/.test(pw) &&
        /[0-9]/.test(pw) &&
        /[^A-Za-z0-9]/.test(pw) &&
        pw.length > MIN_PASSWORD_LENGTH
      : true

    if (validPassword) {
      setPasswordError('')
    } else {
      setPasswordError(`Insufficient password ( a,A,0,!,8+ )`)
    }
    return validPassword
  }

  const passwordsMatch = (pw: string, checkPw: string) => {
    return pw === checkPw
  }

  const handleSubmit = async () => {
    const [hashPass, hashCheckPass] = await Promise.all([
      sha256(password),
      sha256(checkPassword),
    ])

    if (!email) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Missing email',
      })
      return
    }

    if (!checked) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Must agree to terms of service.',
      })
      return
    }

    // check email and password again
    if (!email || !isValidEmail(email)) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Invalid email.',
      })
    } else if (!password || !isValidPassword(password)) {
      setSnackbar({
        open: true,
        severity: 'error',
        message:
          'Must contain an uppercase, lowercase, number, special character, and be at least 8 characters long.',
      })
    } else if (
      !password ||
      !checkPassword ||
      !passwordsMatch(password, checkPassword)
    ) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Passwords do not match.',
      })
    }

    const result = await createAccount(email, hashPass, hashCheckPass)
    // On success create route to login page
    if (result.success) {
      const jwtData = jwt.decode(result.data || '') as JWT
      const expires =
        jwtData.exp && jwtData.iat
          ? jwtData.exp - jwtData.iat
          : 7 * 24 * 60 * 60 // 1 week default
      // This is secure since we are not storing the entire token, which is needed for verification.
      Cookies.set('jwt', JSON.stringify(jwtData), { expires })
      appReducer({ type: 'jwt', payload: jwtData })
      history.push('/dashboard')
    } else {
      setSnackbar({
        open: true,
        severity: 'error',
        message: result.message,
      })
    }
  }

  return (
    <>
      <Grid container justifyContent="center" alignItems="center">
        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          sx={{
            marginTop: '3em',
          }}
        >
          <Component p={3}>
            <Box p={1}>
              <Typography variant="h5" component="h1">
                Create Account
              </Typography>
            </Box>

            <form
              onSubmit={async (e) => {
                e.preventDefault()
                await handleSubmit()
              }}
            >
              <Grid container justifyContent="center" spacing={2} mt={2}>
                <Grid item xs={12}>
                  <TextField
                    value={email}
                    label="Email"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth={true}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={(e) => isValidEmail(e.target.value)}
                    helperText={emailError}
                    error={!!emailError}
                  ></TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    value={name}
                    label="Name"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth={true}
                    onChange={(e) => setName(e.target.value)}
                  ></TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    value={password}
                    label="Password"
                    type="password"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth={true}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={(e) => isValidPassword(e.target.value)}
                    helperText={passwordError}
                    error={!!passwordError}
                  ></TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    value={checkPassword}
                    label="Re-Enter Password"
                    type="password"
                    variant="outlined"
                    size="small"
                    fullWidth={true}
                    required
                    onChange={(e) => setCheckPassword(e.target.value)}
                  ></TextField>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption">
                    By using Team Automate you agree to our{' '}
                    <Link to="/terms">Terms of Service</Link>
                  </Typography>
                  <Checkbox
                    checked={checked}
                    size="small"
                    onChange={() => setChecked(!checked)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Grid
                    container
                    justifyContent="space-between"
                    style={{ marginTop: '1em' }}
                  >
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleReturn}
                      >
                        Back
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        type="submit"
                      >
                        Submit
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Component>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackBarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={handleSnackBarClose}
          severity={snackbar.severity || 'info'}
          elevation={6}
          variant="filled"
        >
          {snackbar.message || ''}
        </MuiAlert>
      </Snackbar>
    </>
  )
}
